import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { ClipConfigProps, ClipStepsControlProps } from "../../clip-menu";
import { Attribute } from "../../models";
import { AttributeTree } from "./attribute-tree";

export interface DataMapping {
  id: string;
  name: string;
  credits?: number;
  isPremium?: boolean;
  children?: DataMapping[];
  tooltip?: string;
}

// If a node `isPremium: true` then all of the children are also premium
export const dataMappings: DataMapping[] = [
  {
    id: "Address",
    name: "Address",
    children: [
      {
        id: "G-NAF Core",
        name: "G-NAF Core",
        credits: 2,
      },
    ],
  },
  {
    id: "Administrative Boundaries",
    name: "Administrative Boundaries",
    children: [
      {
        id: "localities",
        name: "National Localities",
        credits: 2,
      },
      {
        id: "lga",
        name: "National Local Government Areas",
        credits: 2,
      },
      {
        id: "wards",
        name: "National Wards",
        credits: 2,
      },
    ],
  },
  {
    id: "Built Environment",
    name: "Built Environment",
    children: [
      {
        id: "buildings",
        name: "National Building Footprints",
        credits: 8,
        isPremium: true,
        children: [
          {
            id: "heights",
            name: "Heights & Roofs",
            credits: 4,
            tooltip: "Buildings Footprints is required for Heights and Roofs",
          },
          {
            id: "pools",
            name: "Pools Indicator",
            credits: 4,
            tooltip: " Buildings Footprints is required for Pools Indicator",
          },
          {
            id: "solar",
            name: "Solar Indicator",
            credits: 4,
            tooltip: "Buildings Footprints is required for Solar",
          },
        ],
      },
    ],
  },
  {
    id: "Land Parcels",
    name: "Land Parcels",
    children: [
      {
        id: "cadastre",
        name: "National Cadastre",
        credits: 8,
        isPremium: true,
        children: [
          {
            id: "planning",
            name: "National Planning",
            credits: 4,
            tooltip: "National Cadastre is required for National Planning",
          },
        ],
      },
      {
        id: "property",
        name: "National Property",
        credits: 8,
        isPremium: true,
      },
    ],
  },
];

export const ChooseData = (props: ClipConfigProps & ClipStepsControlProps) => {
  const [selected, setSelected] = useState<Attribute[]>(
    props.clipConfig.attributes ?? []
  );

  useEffect(() => {
    props.setClipConfig((prev) => {
      return {
        ...prev,
        attributes: selected,
      };
    });
  }, [selected]);

  const getDefaultItem = (parentId: string) => {
    switch (parentId) {
      case "buildings":
        return "footprints";
      case "cadastre":
        return "cadastre";
      default:
        return parentId;
    }
  };

  const onSelect = (id: string, _parentId: string | undefined) => {
    // clicking on root element (building, cadastre)
    const parentId = _parentId ?? id;

    if (!_parentId) {
      id = getDefaultItem(parentId);
    }

    const existingItem = selected.find((s) => s.key == parentId);
    if (existingItem) {
      // check if we already have that attribute
      //@ts-ignore doesnt know what type it
      if (existingItem.attributes.includes(id)) return;

      let newArray = [...existingItem.attributes, id];
      if (!newArray.includes(getDefaultItem(parentId))) {
        newArray.push(getDefaultItem(parentId));
      }
      const existingItemIndex = selected.findIndex((s) => s.key == parentId);
      const updatedItems = [...selected];
      updatedItems[existingItemIndex] = {
        key: existingItem.key,
        attributes: newArray,
      } as Attribute;
      setSelected(updatedItems);
    } else {
      setSelected((prev) => [
        ...prev,
        {
          key: parentId,
          attributes: [id, getDefaultItem(parentId)],
        } as Attribute,
      ]);
    }
  };

  const onDeSelect = (id: string, parentId?: string) => {
    if (!parentId) {
      const newItems = selected.filter((item: any) => item.key !== id);
      setSelected(newItems);
      return;
    }

    const existingItem = selected.find((s) => s.key == parentId);
    if (existingItem) {
      let newArray = existingItem.attributes.filter((item: any) => item !== id);
      const existingItemIndex = selected.findIndex((s) => s.key == parentId);
      const updatedItems = [...selected];
      updatedItems[existingItemIndex] = {
        key: existingItem.key,
        attributes: newArray,
      } as Attribute;
      setSelected(updatedItems);
    }
  };

  const handleChange = (checked: boolean, id: string, parentId?: string) => {
    if (checked) {
      onSelect(id, parentId);
    } else {
      onDeSelect(id, parentId);
    }
  };

  return (
    <div className="flex flex-col gap-2 h-full">
      <h5 className="text-lg font-medium">Choose your Data</h5>
      <div className="flex overflow-auto h-full">
        <div className="flex flex-col gap-1 w-full">
          {dataMappings.map((dataMapping) => (
            <Collapsible key={dataMapping.id} defaultOpen>
              <CollapsibleTrigger className="flex items-center gap-1 py-1 text-sm font-medium w-full hover:bg-accent/50 rounded px-1 [&[data-state=open]>svg]:rotate-90">
                <ChevronRight className="size-4 transition-transform" />
                {dataMapping.name}
              </CollapsibleTrigger>
              <CollapsibleContent>
                <AttributeTree
                  onChange={handleChange}
                  selected={selected}
                  dataMappings={dataMapping.children}
                />
              </CollapsibleContent>
            </Collapsible>
          ))}
        </div>
      </div>
      <div className="self-end mt-auto flex pt-2">
        <Button
          variant="ghost"
          onClick={() => props.setActiveStep(Math.max(props.activeStep - 1, 0))}
        >
          Back
        </Button>
        <Button
          id="confirm"
          disabled={selected.length == 0}
          onClick={() => {
            props.setClipConfig((prev) => ({
              ...prev,
              attributes: selected,
            }));
            props.setActiveStep(
              Math.min(props.activeStep + 1, props.maxSteps)
            );
          }}
        >
          Confirm
        </Button>
      </div>
    </div>
  );
};
